import type { NextPage } from 'next';
import type { ReactElement, ReactNode } from 'react';

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
}

export interface QueryOptions {
  language: string;
  limit?: number;
  page?: number;
  orderBy?: string;
  sortedBy?: SortOrder;
}

export interface GenerateDescriptionInput {
  prompt: string;
}

export enum ProductType {
  Simple = 'simple',
  Variable = 'variable',
}
export interface MappedPaginatorInfo {
  currentPage: number;
  firstPageUrl: string;
  from: number;
  lastPage: number;
  lastPageUrl: string;
  links: any[];
  nextPageUrl: string | null;
  path: string;
  perPage: number;
  prevPageUrl: string | null;
  to: number;
  total: number;
  hasMorePages: boolean;
}


export interface GetParams {
  slug: string;
  language?: string;
  shop_id?: string;
}

export interface SearchParamOptions {
  rating: string;
  question: string;

  [key: string]: unknown;
}

export interface ProductQueryOptions extends QueryOptions {
  type: string;
  shop_id: string;
  name: string;
  price: string | string[];
  categories: string | string[];
  tags: string | string[];
  types: string | string[];
  aiModels: string | string[];
  is_custom_ai_model: boolean;
  refineBy: string;
  exclude?: number;
}

export interface PopularProductsQueryOptions {
  limit: number;
  shop_id: string;
  type_slug: string;
  range: number;
}

export interface FollowShopPopularProductsQueryOption {
  limit: number;
}

export interface TopShopQueryOptions {
  limit: number;
  name: string;
  range: number;
}

export interface CategoryQueryOptions extends QueryOptions {}

export interface TypeQueryOptions extends QueryOptions {}

export interface AIModelQueryOptions extends QueryOptions {}

export interface WishlistQueryOptions extends QueryOptions {}

export interface MyReportsQueryOptions extends QueryOptions {}

export interface MyQuestionQueryOptions extends QueryOptions {}

export interface ShopQueryOptions extends QueryOptions {
  is_active?: number;
}

export interface FollowedShopsQueryOptions extends QueryOptions {}

export interface OrderQueryOptions extends QueryOptions {
  orderBy: string;
  sortedBy: string;
}

export interface WishlistQueryOption extends QueryOptions {}


export interface GenerateImageQueryOptions {
  product_id: string;
  language: string;
  is_public?: boolean;
  enabled?: boolean;
  limit?: number;
  page?: number;
}

export interface ReviewQueryOptions extends QueryOptions {
  product_id: string;
  rating?: string;
  shop_id?: string;
  initialData?: Review[];
}

export interface QuestionQueryOptions extends QueryOptions {
  product_id: string;
  question?: string;
}

export type NextPageWithLayout<P = {}> = NextPage<P> & {
  authorization?: boolean;
  getLayout?: (page: ReactElement) => ReactNode;
};

export interface PaginatorInfo<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: any[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface SEO {
  metaTitle: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: Attachment;
  twitterHandle: string;
  twitterCardType: string;
  metaTags: string;
  canonicalUrl: string;
}

export interface Settings {
  id: string;
  options: {
    siteTitle: string;
    siteSubtitle: string;
    currency: string;
    logo: Attachment;
    seo: SEO;
    contactDetails: ContactDetails;
    useOtp: Boolean;
    [key: string]: string | any;
  };
}

export interface GeneratedImage {
  id: string;
  user_id: string;
  product_id: string;
  prompt: string;
  image_size: string;
  created_at: Date;
  image_urls: {
    original: string;
    thumbnail: string;
  }[];
  additional_lora_products: {
    id: number;
    name: string;
    image: Attachment;
  }[];
  is_public: boolean;
  my_feedback: {
    id: string;
    user_id: string;
    positive: boolean;
    negative: boolean;
    metadata: {
      liked_image_indexes: number[];
    };
  } | null;
}

export interface ContactDetails {
  socials: [ShopSocials];
  contact: string;
  location: Location;
  website: string;
}

export interface ShopSocials {
  icon: string;
  url: string;
}

export interface Location {
  lat: number;
  lng: number;
  city: string;
  state: string;
  country: string;
  zip: string;
  formattedAddress: string;
}

export interface Attachment {
  id: string;
  original: string;
  thumbnail: string;
  __typename?: string;
}

export interface Shop {
  id: string;
  name: string;
  slug: string;
  description: string;
  orders_count: number;
  products_count: number;
  logo: Attachment;
  cover_image: Attachment;
  settings: {
    socials: {
      icon: string;
      url: string;
    }[];
  };
  address: {
    street_address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  owner: {
    id: string;
    name: string;
    email: string;
  };
}

export interface Subscription {
  id: string;
  name: string;
  interval: 'month' | 'year';
}

export interface User {
  id: string;
  name: string;
  email: string;
  shops: Shop[];
  profile: {
    id: string;
    bio: string;
    contact: string;
    avatar: Attachment;
  };
  wallet?: {
    available_points: number;
    available_train_model_points: number;
    created_at: string;
    customer_id: number;
    id: number;
    points_used: number;
    total_points: number;
    available_points_to_currency: number;
    updated_at: string;
  };
  subscriptions?: Subscription[];
  role: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateProfileInput {
  id: string;
  name: string;
  profile: {
    id?: string;
    bio?: string;
    contact?: string;
    avatar?: Attachment | null;
  };
}

export interface ChangePasswordInput {
  oldPassword: string;
  newPassword: string;
}

export interface ContactInput {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
}

export interface LoginUserInput {
  email: string;
  password: string;
}

export interface RegisterUserInput {
  name: string;
  email: string;
  password: string;
}

export interface ForgetPasswordInput {
  email: string;
}

export interface ResetPasswordInput {
  token: string;
  email: string;
  password: string;
}

export interface VerifyForgetPasswordTokenInput {
  token: string;
  email: string;
}

export interface PasswordChangeResponse {
  success: boolean;
  message: string;
}

export interface AuthResponse {
  token: string;
  permissions: string[];
}

export interface CreateSubscriberInput {
  email: string;
  tags?: string[];
  portofolioUrl?: string;
  seller?: boolean;
}

export interface CreateContactUsInput {
  name: string;
  email: string;
  subject: string;
  description: string;
}

export interface CreateAbuseReportInput {
  model_id: string;
  model_type: string;
  message: string;
}

export interface CreateFeedbackInput {
  model_id: string;
  model_type: string;
  positive?: boolean;
  negative?: boolean;
}

export interface CreateQuestionInput {
  question: string;
  product_id: string;
  shop_id: string;
}

export interface CreateReviewInput {
  product_id: string;
  shop_id: string;
  order_id: string;
  comment?: string;
  rating: number;
  photos?: Attachment[];
}

export interface UpdateReviewInput extends CreateReviewInput {
  id: string;
}

export interface ReviewResponse {
  product_id: string;
}

interface ConnectProductOrderPivot {
  product_id: string | number;
  order_quantity: number;
  unit_price: number;
  subtotal: number;
}

export interface CreateOrderInput {
  amount: number;
  total: number;
  paid_total: number;
  customer_contact: string | null;
  products: ConnectProductOrderPivot[];
  sales_tax: number;
  payment_gateway: PaymentGateway;
  use_wallet_points: boolean;
  payment_id?: string;
  shop_id?: string;
  customer_id?: string;
  isFullWalletPayment: boolean;
}

export enum PaymentGateway {
  FULL_WALLET_PAYMENT = 'FULL_WALLET_PAYMENT',
  STRIPE = 'STRIPE',
  PAYPAL = 'PAYPAL',
  RAZORPAY = 'RAZORPAY',
  MOLLIE = 'MOLLIE',
  PAYSTACK = 'PAYSTACK',
  BITPAY = 'BITPAY',
  COINBASE = 'COINBASE',
}

export enum OrderStatus {
  PENDING = 'order-pending',
  // PROCESSING = 'order-processing',
  COMPLETED = 'order-completed',
  CANCELLED = 'order-cancelled',
  REFUNDED = 'order-refunded',
  FAILED = 'order-failed',
  // AT_LOCAL_FACILITY = 'order-at-local-facility',
  // OUT_FOR_DELIVERY = 'order-out-for-delivery',
}

export enum PaymentStatus {
  PENDING = 'payment-pending',
  PROCESSING = 'payment-processing',
  SUCCESS = 'payment-success',
  FAILED = 'payment-failed',
  REVERSAL = 'payment-reversal',
  WALLET = 'payment-wallet',
}

export interface PaymentIntent {
  id: number | string;
  order_id: number | string;
  payment_gateway: PaymentGateway;
  tracking_number: string;
  payment_intent_info: PaymentIntentInfo;
}

export interface IOrderPaymentSummery {
  used_wallet_amount: number;
  is_payment_gateway_use: boolean;
  gateway_payment: number;
  amount_due: number;
  is_full_paid: boolean;
}

export interface PaymentIntentInfo {
  client_secret?: string;
  payment_id?: string;
  is_redirect?: boolean;
  redirect_url?: string;
  amount?: string;
  currency: string;
}

export interface CheckoutVerificationInput {
  amount: number;
  products: ConnectProductOrderPivot[];
}

export interface VerifiedCheckoutResponse {
  total_tax: number;
  shipping_charge: number;
  unavailable_products: string[];
  wallet_currency: number;
  wallet_amount: number;
}

export interface RatingCount {
  rating: number;
  total: number;
}

export interface Product {
  id: string;
  name: string;
  views_count: number;
  generations_count: number;
  slug: string;
  description: string;
  thumbnail_text: string;
  price: number;
  sale_price: number;
  orders_count: number;
  total_downloads: number;
  image: Attachment;
  gallery: Attachment[];
  shop: Shop;
  created_at: string;
  updated_at: string;
  preview_url: string;
  reviews: Review[];
  shop_reviews: Review[];
  my_review: Review[];
  shop_id: number;
  rating_count: RatingCount[];
  store_review_amount: number;
  store_review_score: number;
  total_reviews: number;
  ratings: number;
  tags: Tag[];
  categories: Category[];
  type_id: number;
  type: {
    id: string;
    name: string;
    slug: string;
  };
  type_slug: string;
  ai_model: {
    id: string;
    name: string;
    icon: any;
    slug: string;
  };
  ai_model_id: number;
  ai_model_version: {
    id: string;
    name: string;
    icon: any;
    slug: string;
  };
  ai_model_version_slug: string;
  language: string;
  in_stock: number;
  is_external: boolean;
  external_product_url: string;
  external_product_button_text: string;
  has_tips: boolean;
  tested: boolean;
  model_version: string;
  word_count: number;
  order_id: number | null;
  prompt_template: string;
  status: ProductStatus;
  prompt_example: {
    id: number;
    text: string;
    original: string;
    thubnail: string;
  }[];
  tips: string | null;
  is_custom_ai_model: string | boolean;
  diffusers_lora_file: any;
  config_file: any;
  debug_caption_files: any;
  experimental_multi_checkpoints: any;
  allow_model_download: boolean;
  training_status: TrainingStatus;
  training_samples: any;
  training_subject_type: TrainingSubjectType;
  trigger_word: string;
  tag_slugs: string[];
  default_image_size: string;
  hidden_fields: any;
}


export enum TrainingStatus {
	DRAFT = 'draft',
	IN_QUEUE = 'in_queue',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ERROR = 'error',
}


export enum TrainingSubjectType {
  MAN = 'man',
  WOMAN = 'woman',
  CHILD = 'child',
  PRODUCT = 'product',
  STYLE = 'style',
  OBJECT = 'object',
  FONT = 'font',
  PET = 'pet',
  FOOD = 'food',
}


export interface AttachmentInput {
  thumbnail: string;
  original: string;
  id?: string;
}

export enum ProductStatus {
  Publish = 'publish',
  Draft = 'draft',
  UnderReview = 'under_review',
  Approved = 'approved',
  UnPublish = 'unpublish',
  Rejected = 'rejected',
}
export enum GPTType {
  REGULAR,
  CHAT,
  // Add other types if there are any
}

export enum StableDiffusionSampler {
  NONE,
  DDIM,
  DDPM,
  K_DPMPP_2M,
  K_DPMPP_2S_ANCESTRAL,
  K_DPM_2,
  K_DPM_2_ANCESTRAL,
  K_EULER,
  K_EULER_ANCESTRAL,
  K_HEUN,
  K_LMS,
  // Add other samplers if there are any
}

export type ModelType = 'Person' | 'Product' | 'Style' | 'Pet';

export interface CreateProductTraining {
  name: string;
  shop_id: string;
  // status: ProductStatus;
  // type_id: string;
  training_subject_type: TrainingSubjectType;
  // product_type: string;
  // ai_model_id: string;
  // ai_model_version_id: string;
  training_samples?: AttachmentInput[];
  image_width?: number;
  image_height?: number;
}

export interface CreateProduct {
  name: string;
  slug: string;
  // model_id: string; TODO: add
  type_id: string;
  price: any;
  sale_price?: any;
  quantity?: number;
  unit: string;
  description?: string;
  thumbnail_text?: string;
  categories?: string[];
  // variations?: AttributeProductPivot[];
  in_stock?: boolean;
  is_taxable?: boolean;
  author_id?: string;
  // digital_file?: DigitalFileInput;
  external_product_button_text?: string;
  external_product_url?: string;
  is_external?: boolean;
  manufacturer_id?: string;
  max_price?: number;
  min_price?: number;
  // variation_options?: UpsertVariationsHasMany;
  video: {
    url: string;
  }[];
  prompt_template: string;
  tips: string;
  prompt_example: {
    text: string;
    url?: string;
    original?: string;
    uploaded_image?: AttachmentInput;
  }[];
  sku?: string;
  gallery?: AttachmentInput[];
  image?: AttachmentInput;
  status?: ProductStatus;
  height?: string;
  length?: string;
  width?: string;
  shop_id?: string;
  gpt_type?: GPTType; // nullable
  preview_input?: string; // nullable
  preview_output?: string; // nullable
  gpt_share_link?: string; // nullable
  image_width?: string; // nullable
  image_height?: string; // nullable
  guidance_scale?: number; // nullable, 'double' in PHP is equivalent to 'number' in TypeScript
  prompt_magic?: boolean; // default false
  photo_real?: boolean; // default false
  alchemy?: boolean; // default false
  negative_prompt?: string; // nullable
  stable_diffusion_sampler?: StableDiffusionSampler; // nullable
  seed?: string; // nullable
  clip_guidance?: boolean; // nullable
  max_tokens?: number; // nullable, 'integer' in PHP is equivalent to 'number' in TypeScript
  temperature?: number; // nullable
  top_p?: number; // nullable
  repetition_penalty?: number; // nullable
  hidden_fields?: any;
}

export interface ProductPaginator extends PaginatorInfo<Product> {}

export interface ReportsPaginator extends PaginatorInfo<Question> {}

export interface Category {
  id: string;
  name: string;
  slug: string;
  translated_languages: string[];
  parent?: number;
  children: Category[];
  details?: string;
  image?: Attachment;
  icon?: string;
  type: Type;
  products: Product[];
  created_at: string;
  updated_at: string;
}

export interface Type {
  id: string;
  name: string;
  icon: string;
  slug: string;
  promotional_sliders?: Attachment;
  created_at: string;
  updated_at: string;
  translated_languages: string[];
}

export interface AIModel {
  id: string;
  name: string;
  icon: string;
  slug: string;
  promotional_sliders?: Attachment;
  created_at: string;
  updated_at: string;
  translated_languages: string[];
  type_id: string;
}

export interface CategoryPaginator extends PaginatorInfo<Category> {}

export interface TypePaginator extends PaginatorInfo<Type> {}

export interface ShopPaginator extends PaginatorInfo<Shop> {}

export interface Order {
  id: number | string;
  tracking_number: string;
  customer_id: number | string;
  amount: number;
  children: Order[] | undefined;
  total: number;
  paid_total: number;
  payment_gateway: string;
  products: Product[];
  created_at: Date;
  updated_at: Date;
  payment_intent?: PaymentIntent;
  order_status: string;
  payment_status: string;
  wallet_point: {
    amount?: number;
  };
  sales_tax: number;
}

export interface DigitalFile {
  id: string;
  fileable: Product;
  attachment_id: number;
}

export interface OrderedFile {
  id: string;
  purchase_key: string;
  digital_file_id: string;
  customer_id: string;
  order_id: string;
  file: DigitalFile;
  created_at: string;
  updated_at: string;
  tracking_number: string;
  order: {
    payment_status: string;
    tracking_number: string;
    id: string;
  };
}

export interface Feedback {
  id: string;
  user_id: string;
  model_type: string;
  model_id: string;
  positive: boolean;
  negative: boolean;
  created_at: string;
  updated_at: string;
}

export interface Question {
  id: string;
  answer: string;
  my_feedback: Feedback;
  negative_feedbacks_count: number;
  positive_feedbacks_count: number;
  product: Product;
  question: string;
  created_at: string;
  updated_at: string;
}

export interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  photos: Attachment[];
  user: User;
  product: Product;
  shop: Shop;
  feedbacks: Feedback[];
  positive_feedbacks_count: number;
  negative_feedbacks_count: number;
  my_feedback: Feedback;
  order_id?: string;
  created_at: string;
  updated_at: string;
}

export interface Wishlist {
  id: string;
  product: Product;
  product_id: string;
  user: User[];
  user_id: string;
}

export interface TagPaginator extends PaginatorInfo<Tag> {}

export interface OrderPaginator extends PaginatorInfo<Order> {}

export interface GeneratedImagePaginator extends PaginatorInfo<GeneratedImage> {}

export interface OrderedFilePaginator extends PaginatorInfo<OrderedFile> {}

export interface ReviewPaginator extends PaginatorInfo<Review> {}

export interface WishlistPaginator extends PaginatorInfo<Wishlist> {}

export interface QuestionPaginator extends PaginatorInfo<Question> {}

export interface SettingsQueryOptions extends QueryOptions {
  language?: string;
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
  type_id: string;
}

export interface Address {
  city: string;
  country: string;
  state: string;
  street_address: string;
  zip: string;
}

export interface Feedback {
  id: string;
  user_id: string;
  model_type: string;
  model_id: string;
  positive: boolean;
  negative: boolean;
  created_at: string;
  updated_at: string;
}

export interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  photos: Attachment[];
  user: User;
  product: Product;
  shop: Shop;
  feedbacks: Feedback[];
  positive_feedbacks_count: number;
  negative_feedbacks_count: number;
  my_feedback: Feedback;
  created_at: string;
  updated_at: string;
}

export interface CreateReviewInput {
  product_id: string;
  shop_id: string;
  comment?: string;
  rating: number;
  photos?: Attachment[];
}

export interface CreateAbuseReportInput {
  model_id: string;
  message: string;
}

export interface Question {
  id: string;
  answer: string;
  my_feedback: Feedback;
  negative_feedbacks_count: number;
  positive_feedbacks_count: number;
  question: string;
  created_at: string;
  updated_at: string;
}

export interface CreateQuestionInput {
  question: string;
  product_id: string;
  shop_id: string;
}

export interface PaymentIntentCollection {
  tracking_number?: string;
  payment_intent_info?: PaymentIntentInfo;
  payment_gateway?: string;
}

export interface CreateOrderPaymentInput {
  tracking_number: string;
  payment_gateway: string;
}

export interface Card {
  expires: string;
  network: string;
  origin: string;
  owner_name: string;
  payment_gateway_id: number | string;
  default_card: number;
}

export interface DownloadableFile {
  id: string;
  purchase_key: string;
  digital_file_id: string;
  customer_id: string;
  file: DigitalFile;
  created_at: string;
  updated_at: string;
}

export interface DownloadableFilePaginator
  extends PaginatorInfo<DownloadableFile> {}

export interface TagQueryOptions extends QueryOptions {
  type: string;
  name: string;
  parent: number | null;
}

export interface Article {
  id: number;
  attributes: {
    Title: string;
    Slug: string;
    Summary: string;
    Content: string;
    isFeatured: boolean;
    publishedAt: string;
    updatedAt: string;
    createdAt: string;
    image: {
      url: string;
    };
    Thumbnail: Thumbnail;
    categories: {
      data: [
        {
          id: number;
          attributes: {
            Name: string;
            createdAt: string;
            updatedAt: string;
            publishedAt: string;
          };
        }
      ];
    };
    author: {
      data: {
        id: number;
        attributes: {
          Name: string;
          Bio?: string;
          createdAt: string;
          updatedAt: string;
          publishedAt: string;
          Avatar: {
            data: {
              id: number;
              attributes: {
                name: string;
                width: number;
                height: number;
                formats: Formats;
              };
            };
          };
        };
      };
    };
    seo: {
      id: number;
      metaTitle: string;
      metaDescription: string;
      metaImage: {
        data: {
          id: number;
          attributes: {
            name: string;
            alternativeText: string;
            caption: string;
            width: number;
            height: number;
            formats: Formats;
            url: string;
            previewUrl?: string;
            provider: string;
            provider_metadata?: string;
            related?: {
              data: Array<{
                id: number;
                attributes: Record<string, any>;
              }>;
            };
            folder?: {
              data: {
                id: number;
                attributes: {
                  name: string;
                  pathId: number;
                  parent?: {
                    data: {
                      id: number;
                      attributes: Record<string, any>;
                    };
                  };
                  children?: {
                    data: Array<{
                      id: number;
                      attributes: Record<string, any>;
                    }>;
                  };
                  files?: {
                    data: Array<{
                      id: number;
                      attributes: Record<string, any>;
                    }>;
                  };
                  createdAt: string;
                  updatedAt: string;
                };
              };
            };
            createdAt: string;
            updatedAt: string;
          };
        };
      };
      metaSocial: Array<{
        id: number;
        socialNetwork: string;
        title: string;
        description: string;
        image: {
          data: {
            id: number;
            attributes: {
              name: string;
              alternativeText: string;
              caption: string;
              width: number;
              height: number;
              formats: Formats;
              url: string;
              previewUrl?: string;
              provider: string;
              provider_metadata?: string;
            };
          };
        };
      }>;
      keywords: string;
      metaRobots: string;
      structuredData: string;
      metaViewport: string;
      canonicalURL: string;
    };
  };
}

export interface Thumbnail {
  data: {
    id: number;
    attributes: {
      name: string;
      alternativeText: string;
      caption: string;
      width: number;
      height: number;
      formats: Formats;
      url: string;
      previewUrl?: string;
      provider: string;
      provider_metadata?: string;
    };
  };
}

export interface Formats {
  // Add properties for different image formats if needed
  small?: FormatDetails;
  medium?: FormatDetails;
  large?: FormatDetails;
  thumbnail?: FormatDetails;
}

export interface FormatDetails {
  url: string;
  width: number;
  height: number;
  size: number;
  mime: string;
}
